import { FormEvent, useState } from "react";
import styled from "styled-components";
import { TCountryCode } from "countries-list";
import { startOfToday } from "date-fns";
import { Group, Passenger } from "../../../types/common.types";
import validate from "../../../utils/validateForm";
import { submitGroup } from "../../../schema/group";
import { useGlobal } from "../../../context/global";
import { useAlly } from "../../../context/ally";
import AllyService from "../../../services/ally.service";
import { edadEn, errorFechaNacimiento, hoyTexto } from "../../../utils/edad";
import m from "../../../styles/molanturTheme";
import CountryInput from "../../common/CountryInput";
import { Badge, Button, DayStrip, Field, Icon, Label, Notice, Row, Segmented, Stepper, WizardShell } from "../ui";

type GuestMode = 'manual' | 'scan'

const emptyGuest = (): Partial<Passenger> => ({
    name: '',
    nationality: 'CL' as TCountryCode,
    email: '',
    phone: '',
    rut_passport: '',
    hotel: '',
    remark: '',
    birth_date: '',
})

const SEXOS = [{ value: 'F' as const, label: 'Femenino' }, { value: 'M' as const, label: 'Masculino' }, { value: 'O' as const, label: 'Otro' }]

// Fecha de nacimiento obligatoria; si el aliado no la sabe ("No sé su fecha"), basta la edad. La
// piden las listas de los parques y la entrada se cobra por edad.
const tieneEdad = (guest: Partial<Passenger>, sinFecha: boolean) => sinFecha
    ? guest.age !== undefined && guest.age !== null && (guest.age as any) !== '' && guest.age >= 0
    : !!guest.birth_date && !errorFechaNacimiento(guest.birth_date)

// Qué le falta a un huésped, con el nombre del campo (23/10/2026). Antes solo decía "Falta" y un aviso
// con todos los campos: un aliado escribió que había puesto todo y le seguía faltando un dato (casi
// siempre el sexo, que son tres botones sin nada marcado).
export function faltanDatos(guest: Partial<Passenger>, sinFecha = false): string[] {
    const faltan: string[] = []
    if ((guest.name || '').trim().length <= 1) faltan.push('nombre')
    if (!guest.sex) faltan.push('sexo')
    if (!tieneEdad(guest, sinFecha)) faltan.push(sinFecha ? 'edad' : guest.birth_date ? 'fecha de nacimiento válida' : 'fecha de nacimiento')
    // El email es opcional desde el 23/10/2026 (igual que para los vendedores desde el 16/10).
    return faltan
}

const isReady = (guest: Partial<Passenger>, sinFecha = false) => faltanDatos(guest, sinFecha).length === 0

const lista = (cosas: string[]) => cosas.length <= 1 ? cosas.join('') : `${cosas.slice(0, -1).join(', ')} y ${cosas[cosas.length - 1]}`

// Paso 1 de "Agregar huéspedes": quiénes van y qué día. Al continuar se crean los pasajeros y el
// grupo (igual que antes); el tour y la entrada se eligen en los pasos siguientes.
export default function CreateGroup() {
    const { setMessage, setIsLoading } = useGlobal()
    const { currentDay, setNewGroup, setNewGroupReservationData, setIsCreateGroupModalOpen } = useAlly()

    const [date, setDate] = useState<Date | undefined>(currentDay || startOfToday())
    const [guests, setGuests] = useState<Partial<Passenger>[]>([emptyGuest()])
    const [modes, setModes] = useState<GuestMode[]>(['manual'])
    const [sinFecha, setSinFecha] = useState<boolean[]>([false])
    const [openGuest, setOpenGuest] = useState<number>(0)
    const [showMissing, setShowMissing] = useState(false)

    // Fecha de nacimiento escrita a medias (ej. sin el año): el campo queda vacío sin avisar.
    const [fechaIncompleta, setFechaIncompleta] = useState<boolean[]>([false])

    // Cambiar la cantidad agrega o quita tarjetas sin tocar las que ya están escritas.
    function setCount(count: number) {
        setGuests(prev => prev.length >= count ? prev.slice(0, count) : prev.concat(Array.from({ length: count - prev.length }, emptyGuest)))
        setModes(prev => prev.length >= count ? prev.slice(0, count) : prev.concat(Array.from({ length: count - prev.length }, () => 'manual' as GuestMode)))
        setSinFecha(prev => prev.length >= count ? prev.slice(0, count) : prev.concat(Array.from({ length: count - prev.length }, () => false)))
        setFechaIncompleta(prev => prev.length >= count ? prev.slice(0, count) : prev.concat(Array.from({ length: count - prev.length }, () => false)))
    }

    function setNoSabeFecha(index: number, noSabe: boolean) {
        setSinFecha(prev => prev.map((current, i) => i === index ? noSabe : current))
    }

    function setGuest(index: number, patch: Partial<Passenger>) {
        setGuests(prev => prev.map((guest, i) => i === index ? { ...guest, ...patch } : guest))
    }

    function setMode(index: number, mode: GuestMode) {
        setModes(prev => prev.map((current, i) => i === index ? mode : current))
    }

    function close() {
        const hasData = guests.some(guest => (guest.name || '').trim() || (guest.email || '').trim())
        if (hasData && !window.confirm('¿Salir sin guardar los huéspedes?')) return
        setIsCreateGroupModalOpen(false)
    }

    async function onSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        const firstMissing = guests.findIndex((guest, i) => !isReady(guest, sinFecha[i]))
        if (!date || firstMissing >= 0) {
            setShowMissing(true)
            if (firstMissing >= 0) setOpenGuest(firstMissing)
            return
        }
        setIsLoading(true)
        try {
            // El teléfono y el email son opcionales: si no se escribieron, no se mandan.
            // Con fecha de nacimiento no se manda la edad (la calcula el backend); sin fecha, solo la edad.
            const members = guests.map(({ phone, email, birth_date, age, ...guest }, i) => ({
                ...guest,
                ...((phone || '').trim() ? { phone } : {}),
                ...((email || '').trim() ? { email: (email || '').trim() } : {}),
                ...(sinFecha[i] ? { age } : { birth_date }),
            }))
            validate(submitGroup, members, true)
            let newGroupCorrectID: Partial<Passenger>[] = []
            for (let i = 0; i < members.length; i++) {
                const passsenger = members[i];
                const new_passenger_id = (await AllyService.createPassenger(passsenger)).data
                const new_passenger = {
                    ...passsenger,
                    ...new_passenger_id
                }
                newGroupCorrectID.push(new_passenger)
            }
            setNewGroup(newGroupCorrectID)
            const newGroup: Group = await (await AllyService.createGroup()).data
            setNewGroupReservationData(prevData => ({
                ...prevData,
                group_id: newGroup.id,
                reservation_date: date,
            }))
            setMessage({
                type: 'success',
                content: 'Huéspedes guardados. Ahora elige el tour.'
            })
        } catch (error) {
            console.log(error);

            setMessage({
                type: 'error',
                content: 'Revisa los datos de los huéspedes: hay campos con errores.'
            })
        } finally {
            setTimeout(() => {
                setMessage(undefined)
            }, 2500)
            setIsLoading(false)
        }
    }

    const ready = guests.filter((guest, i) => isReady(guest, sinFecha[i])).length
    const incompletos = guests.map((guest, i) => ({ i, guest, faltan: faltanDatos(guest, sinFecha[i]) })).filter(x => x.faltan.length)

    return (
        <WizardShell
            step={1}
            title="Huéspedes y fecha"
            onClose={close}
            onSubmit={onSubmit}
            footer={<Button type="submit" full size="lg" icon="arrowRight">Continuar</Button>}
        >
            <Step>
                <Label className="section">Fecha</Label>
                <DayStrip day={date} onChange={setDate} allowPast={false} />

                <Label className="section">Número de huéspedes</Label>
                <Stepper value={guests.length} onChange={setCount} min={1} max={20} label="Número de huéspedes" />

                <div className="guests-head">
                    <Label>Detalle de huéspedes</Label>
                    <span>{ready} de {guests.length} {guests.length === 1 ? 'listo' : 'listos'}</span>
                </div>

                {showMissing && incompletos.length > 0 && (
                    <Notice $tone="warn" role="alert" style={{ marginBottom: 10 }}>
                        <Icon name="info" size={16} />
                        <span>
                            {incompletos.map(({ i, guest, faltan }) => (
                                <span key={i} style={{ display: 'block' }}>
                                    {(guest.name || '').trim() || (i === 0 ? 'Huésped principal' : `Huésped ${i + 1}`)}: falta {lista(faltan)}.
                                </span>
                            ))}
                        </span>
                    </Notice>
                )}

                {guests.map((guest, index) => {
                    const done = isReady(guest, sinFecha[index])
                    const errorFecha = guest.birth_date ? errorFechaNacimiento(guest.birth_date) : null
                    const edad = guest.birth_date && !errorFecha ? edadEn(guest.birth_date) : null
                    const isOpen = openGuest === index
                    const title = (guest.name || '').trim() || (index === 0 ? 'Huésped principal' : `Huésped ${index + 1}`)
                    return (
                        <GuestCard key={index} $open={isOpen}>
                            <button type="button" className="guest-head" aria-expanded={isOpen} onClick={() => setOpenGuest(isOpen ? -1 : index)}>
                                <span className={`num ${done ? 'done' : ''}`}>{done ? <Icon name="check" size={13} stroke={2.6} /> : index + 1}</span>
                                <span className="guest-title">{title}</span>
                                {showMissing && !done && <Badge $tone="warn">Falta {lista(faltanDatos(guest, sinFecha[index]))}</Badge>}
                                <Icon name="chevronDown" size={17} stroke={2} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
                            </button>

                            {isOpen && (
                                <div className="guest-body">
                                    <div className="mode">
                                        <Segmented
                                            label={`Cómo ingresar los datos de ${title}`}
                                            options={[{ value: 'scan' as GuestMode, label: 'Escanear pasaporte' }, { value: 'manual' as GuestMode, label: 'Ingresar a mano' }]}
                                            value={modes[index]}
                                            onChange={mode => setMode(index, mode)}
                                        />
                                    </div>

                                    {modes[index] === 'scan' ? (
                                        <div className="scan">
                                            <span className="scan-icon"><Icon name="camera" size={24} stroke={2} /></span>
                                            <strong>Escaneo de pasaporte en construcción</strong>
                                            <p>Pronto podrás tomarle una foto al pasaporte y los datos se llenarán solos. Por ahora, ingrésalos a mano.</p>
                                            <Button kind="ghost" onClick={() => setMode(index, 'manual')}>Ingresar a mano</Button>
                                        </div>
                                    ) : (
                                        <>
                                            <Field label="Nombre completo*" name="name" placeholder="Como aparece en el pasaporte" autoComplete="off"
                                                value={guest.name || ''} onChange={e => setGuest(index, { name: e.target.value })} />
                                            <Field label="RUT / Pasaporte" name="rut_passport" placeholder="N.º de documento" autoComplete="off"
                                                value={guest.rut_passport || ''} onChange={e => setGuest(index, { rut_passport: e.target.value })} />
                                            {sinFecha[index] ? (
                                                <div className="nacimiento">
                                                    <Field label="Edad*" name="age" type="number" inputMode="numeric" min={0} max={110} placeholder="Años"
                                                        hint="Si puedes, pide la fecha: algunos parques la exigen."
                                                        value={guest.age ?? ''} onChange={e => setGuest(index, { age: e.target.value === '' ? undefined : Number(e.target.value) })} />
                                                    <button type="button" className="link" onClick={() => setNoSabeFecha(index, false)}>Ingresar fecha de nacimiento</button>
                                                </div>
                                            ) : (
                                                <div className="nacimiento">
                                                    <Field label="Fecha de nacimiento*" name="birth_date" type="date" max={hoyTexto()}
                                                        hint={errorFecha ? errorFecha : fechaIncompleta[index] && !guest.birth_date ? 'Fecha incompleta: revisa día, mes y año.' : edad !== null ? `${edad} ${edad === 1 ? 'año' : 'años'}` : undefined}
                                                        value={guest.birth_date || ''}
                                                        onChange={e => { setGuest(index, { birth_date: e.target.value }); setFechaIncompleta(prev => prev.map((v, i) => i === index ? false : v)) }}
                                                        onBlur={e => { const incompleta = (e.target as HTMLInputElement).validity.badInput; setFechaIncompleta(prev => prev.map((v, i) => i === index ? incompleta : v)) }} />
                                                    <button type="button" className="link" onClick={() => setNoSabeFecha(index, true)}>No sé su fecha</button>
                                                </div>
                                            )}
                                            <div className="sexo">
                                                <Label as="span">Sexo*</Label>
                                                <Segmented label="Sexo" options={SEXOS} value={(guest.sex || undefined) as any}
                                                    onChange={sex => setGuest(index, { sex })} />
                                                {showMissing && !guest.sex && <span className="falta">Elige una opción.</span>}
                                            </div>
                                            {/* Se busca escribiendo, en español o inglés, igual que en el formulario del vendedor
                                                (23/10/2026). Antes era una lista en inglés con Chile ya elegido: quedaba en Chile. */}
                                            <div className="nacionalidad">
                                                <Label as="span">Nacionalidad</Label>
                                                <CountryInput buscable sinEtiqueta label="Nacionalidad" name="nationality" value={guest.nationality as TCountryCode}
                                                    onChange={e => setGuest(index, { nationality: e.target.value as TCountryCode })} />
                                            </div>
                                            <Field label="Hotel" name="hotel" placeholder="Dónde se hospeda" autoComplete="off"
                                                value={guest.hotel || ''} onChange={e => setGuest(index, { hotel: e.target.value })} />
                                            <Row>
                                                <Field label="Email" name="email" type="email" inputMode="email" placeholder="nombre@email.com" autoComplete="off"
                                                    value={guest.email || ''} onChange={e => setGuest(index, { email: e.target.value })} />
                                                <Field label="Teléfono" name="phone" type="tel" inputMode="tel" placeholder="+56 9 …" autoComplete="off"
                                                    value={guest.phone || ''} onChange={e => setGuest(index, { phone: e.target.value })} />
                                            </Row>
                                            <Field label="Remarks" name="remark" placeholder="Alergias, dieta, movilidad…" autoComplete="off" maxLength={150}
                                                hint="Lo recibe la agencia junto con la reserva. Anota aquí alergias o necesidades especiales."
                                                value={guest.remark || ''} onChange={e => setGuest(index, { remark: e.target.value })} />
                                        </>
                                    )}
                                </div>
                            )}
                        </GuestCard>
                    )
                })}
            </Step>
        </WizardShell>
    )
}

const Step = styled.div`
    .nacimiento {
        position: relative;
        .link {
            position: absolute;
            top: 0;
            right: 0;
            border: none;
            background: none;
            padding: 0;
            font: inherit;
            font-size: 12px;
            font-weight: 600;
            color: ${m.color.primaryDeep};
            text-decoration: underline;
            cursor: pointer;
        }
    }
    .sexo {
        margin-bottom: 14px;
        > span { display: block; margin-bottom: 6px; }
        > .falta { margin: 6px 0 0; font-size: 12px; font-weight: 600; color: ${m.color.warn}; }
    }
    .nacionalidad {
        margin-bottom: 14px;
        > span { display: block; margin-bottom: 7px; }
        > div { width: 100%; }
        .campo input {
            height: 50px;
            padding: 0 13px;
            background: ${m.color.surface};
            border: 1.5px solid ${m.color.lineStrong};
            border-radius: ${m.radius.md};
            font-family: ${m.font.text};
            font-weight: 500;
            color: ${m.color.ink};
            outline: none;
            &:focus { border-color: ${m.color.primaryDeep}; }
        }
        small { display: block; margin-top: 6px; font-size: 12px; font-weight: 600; color: ${m.color.warn}; }
    }
    .section { margin: 18px 0 9px; }
    .section:first-child { margin-top: 0; }
    .guests-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 10px;
        margin: 22px 0 10px;
        > span {
            font-size: 12px;
            font-weight: 600;
            color: ${m.color.inkFaint};
            white-space: nowrap;
        }
    }
`

const GuestCard = styled.div<{ $open: boolean }>`
    margin-bottom: 10px;
    overflow: hidden;
    background: ${m.color.surface};
    border: 1.5px solid ${p => p.$open ? m.color.ink : m.color.line};
    border-radius: ${m.radius.md};

    .guest-head {
        width: 100%;
        min-height: 50px;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 13px;
        border: none;
        background: transparent;
        text-align: left;
        cursor: pointer;
        font-family: inherit;
        color: ${m.color.inkFaint};
    }
    .num {
        width: 26px;
        height: 26px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: ${m.color.primarySoft};
        color: ${m.color.inkFaint};
        font-family: ${m.font.label};
        font-weight: 600;
        font-size: 12px;
    }
    .num.done {
        background: ${m.color.successSoft};
        color: ${m.color.success};
    }
    .guest-title {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: 600;
        font-size: 14.5px;
        color: ${m.color.ink};
    }
    .guest-body { padding: 0 13px 4px; }
    .mode { margin-bottom: 14px; }
    .scan {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        margin-bottom: 14px;
        padding: 22px 16px;
        text-align: center;
        border: 2px dashed ${m.color.lineStrong};
        border-radius: ${m.radius.md};
        background: ${m.color.surface2};
        strong {
            font-weight: 600;
            font-size: 14.5px;
            color: ${m.color.ink};
        }
        p {
            margin: 0 0 6px;
            max-width: 300px;
            font-size: 12.5px;
            line-height: 1.4;
            color: ${m.color.inkFaint};
        }
    }
    .scan-icon {
        width: 52px;
        height: 52px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: ${m.color.primary};
        color: ${m.color.onPrimary};
    }
`
